import { useState, useEffect } from 'react';
import { Button } from '@shadcn/components/ui/button';

const ToleranceForm = ({ 
  selectedCategory, 
  toleranceSetting, 
  onSave, 
  onDelete, 
  saving 
}) => {
  const [formData, setFormData] = useState({
    tolerancePercent: '',
    toleranceValue: ''
  });
  const [errors, setErrors] = useState({});

  // Update form when tolerance setting changes
  useEffect(() => {
    if (toleranceSetting) {
      setFormData({
        tolerancePercent: toleranceSetting.tolerancePercent || '',
        toleranceValue: toleranceSetting.toleranceValue || ''
      });
    } else {
      setFormData({
        tolerancePercent: '',
        toleranceValue: ''
      });
    }
    setErrors({});
  }, [toleranceSetting, selectedCategory]);

  const validateForm = () => {
    const newErrors = {};

    // Validate tolerance percent
    const percent = parseFloat(formData.tolerancePercent);
    if (!formData.tolerancePercent) {
      newErrors.tolerancePercent = 'Tolerance percentage is required';
    } else if (isNaN(percent)) {
      newErrors.tolerancePercent = 'Must be a valid number';
    } else if (percent < 0 || percent > 100) {
      newErrors.tolerancePercent = 'Must be between 0 and 100';
    }

    // Validate tolerance value
    const value = parseFloat(formData.toleranceValue);
    if (!formData.toleranceValue) {
      newErrors.toleranceValue = 'Tolerance value is required';
    } else if (isNaN(value)) {
      newErrors.toleranceValue = 'Must be a valid number';
    } else if (value < 0) {
      newErrors.toleranceValue = 'Must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      tolerancePercent: parseFloat(formData.tolerancePercent),
      toleranceValue: parseFloat(formData.toleranceValue)
    };

    onSave(selectedCategory, submitData);
  };

  const handleReset = () => {
    if (toleranceSetting) {
      setFormData({
        tolerancePercent: toleranceSetting.tolerancePercent || '',
        toleranceValue: toleranceSetting.toleranceValue || ''
      });
    } else {
      setFormData({
        tolerancePercent: '',
        toleranceValue: ''
      });
    }
    setErrors({});
  };

  const handleDelete = () => {
    onDelete(selectedCategory);
  };

  const isFormValid = () => {
    return formData.tolerancePercent && 
           formData.toleranceValue && 
           Object.keys(errors).length === 0 &&
           !isNaN(parseFloat(formData.tolerancePercent)) &&
           !isNaN(parseFloat(formData.toleranceValue)) &&
           parseFloat(formData.tolerancePercent) >= 0 &&
           parseFloat(formData.tolerancePercent) <= 100 &&
           parseFloat(formData.toleranceValue) >= 0;
  };

  if (!selectedCategory) {
    return (
      <div className="h-full flex flex-col">
        <div className="border-b border-gray-200 pb-4 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Tolerance Configuration</h3>
          <p className="text-sm text-gray-600 mt-1">Configure tolerance thresholds for inventory accuracy</p>
        </div>
        
        <div className="flex-1 flex flex-col justify-center items-center py-8">
          <div className="text-6xl mb-4 text-gray-300">🎯</div>
          <p className="text-gray-600 font-medium mb-2">Select a category to configure</p>
          <p className="text-sm text-gray-500 text-center">Choose a category from the list on the left to set up tolerance thresholds</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Tolerance Configuration</h3>
        <p className="text-sm text-gray-600 mt-1">
          Setting tolerance for: <span className="font-medium text-gray-900">{selectedCategory}</span>
        </p>
        
        {toleranceSetting && (
          <div className="mt-2 text-xs text-gray-500">
            Last updated: {new Date(toleranceSetting.updatedAt).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tolerance Percentage */}
          <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Tolerance Percentage (%)
          </label>
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={formData.tolerancePercent}
              onChange={(e) => setFormData(prev => ({ ...prev, tolerancePercent: e.target.value }))}
              className={`
                w-full px-4 py-3 border rounded-lg bg-white text-gray-900 
                focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent
                ${errors.tolerancePercent ? 'border-red-400' : 'border-gray-300'}
              `}
              placeholder="e.g., 5.0"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-gray-500 text-sm">%</span>
            </div>
          </div>
          {errors.tolerancePercent && (
            <p className="mt-1 text-sm text-red-600">{errors.tolerancePercent}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Acceptable variance percentage from expected inventory
          </p>
        </div>

        {/* Tolerance Value */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Tolerance Value ($)
          </label>
          <div className="relative">
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.toleranceValue}
              onChange={(e) => setFormData(prev => ({ ...prev, toleranceValue: e.target.value }))}
              className={`
                w-full px-4 py-3 pl-8 border rounded-lg bg-white text-gray-900 
                focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent
                ${errors.toleranceValue ? 'border-red-400' : 'border-gray-300'}
              `}
              placeholder="e.g., 100.00"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 text-sm">$</span>
            </div>
          </div>
          {errors.toleranceValue && (
            <p className="mt-1 text-sm text-red-600">{errors.toleranceValue}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Maximum acceptable dollar value variance
          </p>
        </div>

        {/* Preview */}
        {isFormValid() && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Preview</h4>
            <div className="text-sm text-blue-800">
              <p>Items in <strong>{selectedCategory}</strong> will be considered accurate if variance is:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Within <strong>{formData.tolerancePercent}%</strong> of expected quantity</li>
                <li>Within <strong>${formData.toleranceValue}</strong> of expected value</li>
              </ul>
            </div>
          </div>
        )}

        </form>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-6 border-t border-gray-200 mt-4">
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid() || saving}
          className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-medium"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Saving...
            </>
          ) : (
            <>💾 {toleranceSetting ? 'Update' : 'Save'} Setting</>
          )}
        </Button>

        <Button
          onClick={handleReset}
          variant="outline"
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          🔄 Reset
        </Button>

        {toleranceSetting && (
          <Button
            onClick={handleDelete}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
          >
            🗑️ Delete
          </Button>
        )}
      </div>
    </div>
  );
};

export default ToleranceForm;